import { useEffect, useState, useRef } from 'react';

// SHIFTon — the staff-app refresh signal.
//
// THE PROBLEM THIS SOLVES: every staff screen fetches once, in a
// useEffect with an empty dependency array, and never again. A staff member
// sitting on Schedule while an agent accepts them sees the BottomNav badge
// light up (it polls every 30s) and the screen behind it stay wrong. Tapping
// the badge does nothing either — navigate('/schedule') from '/schedule' does
// not remount the route, so the effect never re-runs.
//
// ⚠️ THIS FILE NEVER FETCHES. It only says "now would be a good time".
// The fetching stays exactly where it already is, on each screen, untouched.
//
// Why a module-level bus rather than a React context: BottomNav is rendered
// INSIDE each screen, not above it, so a provider would have to wrap AppRoutes
// and every screen would still need a hook to read it. The bus is the same
// amount of wiring with no provider, and firing a signal does not re-render
// the tree.

// ── The bus ──────────────────────────────────────────────────────────────────
const listeners = new Set();

export function onRefresh(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// force=true skips the throttle below. Use it for a DELIBERATE action (tapping
// the active tab, tapping a notification). Leave it false for anything
// automatic, so a burst of events is collapsed into one fetch.
export function emitRefresh(force = false) {
  // Copy first: a listener that unsubscribes while the loop is running would
  // otherwise mutate the Set being iterated.
  [...listeners].forEach(fn => {
    try { fn(force); } catch (e) { /* one bad listener must not stop the rest */ }
  });
}

// ── The hook ─────────────────────────────────────────────────────────────────
// Returns an integer that goes up whenever the screen should refetch. Usage:
//
//     const refreshKey = useAutoRefresh();
//     useEffect(() => { ...the existing fetch, unchanged... }, [refreshKey]);
//
// ⚠️ DO NOT USE ON A SCREEN THAT HOLDS FORM STATE. Account.js is deliberately
// excluded: it keeps detailsEditing / bankEditing / sizingEditing and live
// input values, and a background refetch would overwrite what someone is
// halfway through typing, including their account number.
//
// Triggers:
//   visibilitychange  returning to the app from the home screen or another app
//   pageshow          the same thing on iOS, where a bfcache restore does not
//                     always fire visibilitychange
//   the bus           BottomNav's existing 30s poll (only when a count goes
//                     UP), and tapping the tab you are already on
//
// ⚠️ THROTTLE_MS exists because iOS fires visibilitychange and pageshow
// together on a single return to the app. Without it every app-switch costs
// two round trips per screen, across 120 handsets.
const THROTTLE_MS = 3000;

export default function useAutoRefresh() {
  const [key, setKey] = useState(0);
  const last = useRef(0);

  useEffect(() => {
    const bump = (force = false) => {
      const now = Date.now();
      if (!force && now - last.current < THROTTLE_MS) return;
      last.current = now;
      setKey(k => k + 1);
    };

    // Called by both DOM events; both pass an Event object, which bump ignores
    // (an Event is truthy, so it must NOT be forwarded as `force` — hence the
    // wrapper rather than passing bump directly).
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    const off = onRefresh(bump);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
      off();
    };
  }, []);

  return key;
}
